import { type SubmitEvent, useState } from "react";

import { MIN_PASSWORD_LENGTH } from "@/constants/auth";
import type { AuthMetadata } from "@/services/api-client/auth/types";
import { gtmService } from "@/services/gtm-service";
import { useAuthStore } from "@/store/auth.store";

interface Params {
  onSuccess: () => void;
  metadata?: AuthMetadata;
  quizId?: string;
}

interface ReturnValues {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  terms: boolean;
  setTerms: (terms: boolean) => void;
  acceptMarketing: boolean;
  setAcceptMarketing: (acceptMarketing: boolean) => void;
  handleSubmit: (e: SubmitEvent) => Promise<void>;
  isLoading: boolean;
  isValid: boolean;
  error: string | null;
  onSuccess: () => void;
}

export const useSignUp = ({ onSuccess, metadata, quizId }: Params): ReturnValues => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const { signup, isLoading, error, clearError } = useAuthStore();

  const isValid = !!email && !!password && password.length >= MIN_PASSWORD_LENGTH;

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    clearError();
    if (!isValid) return;
    try {
      gtmService.trackSignUpTry({ email, quiz_id: quizId });
      await signup({ email, password, metadata, acceptMarketing });
      gtmService.trackSignUpScs({ email, quiz_id: quizId });
      onSuccess();
    } catch {
      gtmService.trackSignUpFail({ email, quiz_id: quizId });
      // error is in store
    }
  };

  return {
    email,
    setEmail,
    password,
    setPassword,
    terms,
    setTerms,
    acceptMarketing,
    setAcceptMarketing,
    handleSubmit,
    isLoading,
    isValid,
    error,
    onSuccess,
  };
};
